const LOCALSTORAGE_FORM_KEY = 'fek-prediction-id';

/**
 * Checks localstorage whether user has submitted the prediction
 *
 * @param {string} predictionId form ID to check in localstorage
 * @returns {string} prediction options submited
 */
// eslint-disable-next-line id-length
export const getPredictionSubmittedOptions = (predictionId) => {
    return localStorage.getItem(`${LOCALSTORAGE_FORM_KEY}-${predictionId}`);
};

/**
 * Calculates the vote percentage for each option
 * and looks for the winner option.
 *
 * @param {number} votes amount of votes for a prediction option
 * @param {number} totalVotes total votes of the prediction
 * @returns {string} percentage
 */
export const getVotePercentage = (votes, totalVotes) => {
    const hundred = 100;
    if (totalVotes === 0) {
        return '0%';
    }
    const value = (votes / totalVotes) * hundred;
    if (Number.isInteger(value)) {
        return `${value}%`;
    }
    return `${value.toFixed(1)}%`;
};

/**
 * Returns the fan's prediction object if available, otherwise null.
 *
 * @param {object} scorePredictor - The score predictor object containing questions and selected options.
 * @returns {{ entity_a_score: number, entity_b_score: number, times_selected: number } | null} The fan's prediction object if available, otherwise null.
 */
export function getFanPrediction(scorePredictor) {
    const selected = scorePredictor?.questions?.[0]?.selected_options?.[0];
    if (
        typeof selected?.entity_a_score === 'number' &&
        typeof selected?.entity_b_score === 'number'
    ) {
        return {
            entity_a_score: selected.entity_a_score,
            entity_b_score: selected.entity_b_score,
            times_selected: selected.times_selected
        };
    }
    return null;
}

// Returns true if fanPrediction matches the given prediction option (by scores)
/**
 * Checks if the fan's prediction matches the given prediction option by comparing scores.
 *
 * @param {{ entity_a_score: number, entity_b_score: number, times_selected?: number } | null} fanPrediction - The fan's prediction object.
 * @param {{ entity_a_score: number, entity_b_score: number, times_selected?: number }} option - The prediction option to compare against.
 * @returns {boolean} True if the scores match, otherwise false.
 */
export function isFanPredictionMatch(fanPrediction, option) {
    if (!fanPrediction || !option) {
        return false;
    }
    return (
        fanPrediction.entity_a_score === option.entity_a_score &&
        fanPrediction.entity_b_score === option.entity_b_score
    );
}

// Returns true if the given prediction matches the correct answer in scorePredictor
/**
 * Checks if the given prediction matches the correct answer in the scorePredictor object.
 *
 * @param {object} scorePredictor - The score predictor object containing questions and correct options.
 * @param {{ entity_a_score: number, entity_b_score: number }} prediction - The prediction to check for correctness.
 * @returns {boolean} True if the prediction matches the correct answer, otherwise false.
 */
export function isPredictionCorrect(scorePredictor, prediction) {
    const correct = scorePredictor?.questions?.[0]?.correct_options?.[0];
    if (!correct || !prediction) {
        return false;
    }
    return (
        correct.entity_a_score === prediction.entity_a_score &&
        correct.entity_b_score === prediction.entity_b_score
    );
}
